import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const GMDocumentation = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', title: t('admin.gmDocs.overview'), icon: '📋' },
    { id: 'profile', title: t('admin.gmDocs.profile'), icon: '👤' },
    { id: 'team', title: t('admin.gmDocs.team'), icon: '🏀' },
    { id: 'trades', title: t('admin.gmDocs.trades'), icon: '🔄' },
    { id: 'games', title: t('admin.gmDocs.games'), icon: '📊' },
    { id: 'schedule', title: t('admin.gmDocs.schedule'), icon: '📅' },
    { id: 'playoffs', title: t('admin.gmDocs.playoffs'), icon: '🏆' },
    { id: 'settings', title: t('admin.gmDocs.settings'), icon: '⚙️' }
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">{t('admin.gmDocs.overviewTitle')}</h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="font-bold text-lg mb-3 text-blue-600 dark:text-blue-400">🎯 {t('admin.gmDocs.yourResponsibilities')}</h3>
                <ul className="space-y-2 text-sm">
                  <li>• {t('admin.gmDocs.responsibilitiesList.manageRoster')}</li>
                  <li>• {t('admin.gmDocs.responsibilitiesList.enterResults')}</li>
                  <li>• {t('admin.gmDocs.responsibilitiesList.proposeTrades')}</li>
                  <li>• {t('admin.gmDocs.responsibilitiesList.updateProfile')}</li>
                  <li>• {t('admin.gmDocs.responsibilitiesList.followRules')}</li>
                  <li>• {t('admin.gmDocs.responsibilitiesList.participatePlayoffs')}</li>
                </ul>
              </div>
              
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="font-bold text-lg mb-3 text-green-600 dark:text-green-400">🔄 {t('admin.gmDocs.regularTasks')}</h3>
                <ol className="space-y-2 text-sm">
                  <li>1. {t('admin.gmDocs.regularTasksList.checkSchedule')}</li>
                  <li>2. {t('admin.gmDocs.regularTasksList.playGames')}</li>
                  <li>3. {t('admin.gmDocs.regularTasksList.enterResults')}</li>
                  <li>4. {t('admin.gmDocs.regularTasksList.reviewTrades')}</li>
                  <li>5. {t('admin.gmDocs.regularTasksList.monitorStandings')}</li>
                  <li>6. {t('admin.gmDocs.regularTasksList.updateGamertag')}</li>
                </ol>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-800 mb-2">📢 {t('admin.gmDocs.gettingHelp')}</h3>
              <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
                <li>• {t('admin.gmDocs.gettingHelpList.contactAdmins')}</li>
                <li>• {t('admin.gmDocs.gettingHelpList.checkDocs')}</li>
                <li>• {t('admin.gmDocs.gettingHelpList.askGMs')}</li>
                <li>• {t('admin.gmDocs.gettingHelpList.reportBugs')}</li>
              </ul>
            </div>
          </div>
        );

      case 'profile':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">{t('admin.gmDocs.profileTitle')}</h2>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-green-800 mb-2">👤 {t('admin.gmDocs.profileOverview')}</h3>
              <p className="text-green-700 dark:text-green-400 text-sm">
                {t('admin.gmDocs.profileDesc')} {t('admin.gmDocs.profileOverviewDesc')}
              </p>
            </div>

            <div className="space-y-6">
              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-blue-600 dark:text-blue-400">Updating Your Profile</h3>
                <ol className="space-y-3 text-sm">
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                    <div>
                      <strong>Navigate to Settings</strong><br/>
                      Click your username in the top-right → Settings
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                    <div>
                      <strong>Update Profile Information</strong><br/>
                      Edit your first name, gamertag, gaming platform, and Twitch profile
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                    <div>
                      <strong>Save Changes</strong><br/>
                      Click "Update Profile" to save your changes
                    </div>
                  </li>
                </ol>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-purple-600">Profile Fields Explained</h3>
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h4 className="font-semibold mb-3">Required Information:</h4>
                    <ul className="space-y-2">
                      <li>• <strong>First Name:</strong> Your display name in the league</li>
                      <li>• <strong>Gamertag:</strong> Your NBA 2K gaming username</li>
                      <li>• <strong>Platform:</strong> Xbox or PlayStation (for matchmaking)</li>
                      <li>• <strong>Email:</strong> For league communications</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">Optional Information:</h4>
                    <ul className="space-y-2">
                      <li>• <strong>Twitch Profile:</strong> If you stream your games</li>
                      <li>• <strong>Bio/Description:</strong> Tell others about yourself</li>
                      <li>• <strong>Time Zone:</strong> Helps with scheduling games</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-orange-600">Twitch Integration</h3>
                <div className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold text-green-600 dark:text-green-400">Adding Your Twitch Profile:</h4>
                    <ol className="mt-2 space-y-1 ml-4">
                      <li>1. Go to Settings → Profile Information</li>
                      <li>2. Enter your Twitch username (e.g., "yourusername")</li>
                      <li>3. Save your profile</li>
                      <li>4. Your Twitch link will appear on the Team Owners page</li>
                    </ol>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">Benefits:</h4>
                    <ul className="mt-2 space-y-1 ml-4">
                      <li>• Other GMs can watch your gameplay</li>
                      <li>• Builds community engagement</li>
                      <li>• Shows up in public Team Owners directory</li>
                      <li>• Helps promote the league to viewers</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        );

      case 'team':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">{t('admin.gmDocs.teamTitle')}</h2>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-800 mb-2">🏀 Team Management</h3>
              <p className="text-blue-700 dark:text-blue-400 text-sm">
                {t('admin.gmDocs.teamDesc')} Keep track of your players and team performance.
              </p>
            </div>

            <div className="space-y-6">
              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-blue-600 dark:text-blue-400">Viewing Your Team</h3>
                <ol className="space-y-3 text-sm">
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                    <div>
                      <strong>Navigate to Teams Page</strong><br/>
                      Main navigation → Teams → Find your team
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                    <div>
                      <strong>Click Your Team Card</strong><br/>
                      This opens your team's roster page
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                    <div>
                      <strong>Review Roster</strong><br/>
                      See all your players, their ratings, and positions
                    </div>
                  </li>
                </ol>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-green-600 dark:text-green-400">Player Information</h3>
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h4 className="font-semibold mb-3">Roster Page Shows:</h4>
                    <ul className="space-y-2">
                      <li>• <strong>Player Names:</strong> Full names of your players</li>
                      <li>• <strong>Positions:</strong> PG, SG, SF, PF, C</li>
                      <li>• <strong>Overall Rating:</strong> Player skill level (0-99)</li>
                      <li>• <strong>Badge Count:</strong> Number of badges player has</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">Managing Players:</h4>
                    <ul className="space-y-2">
                      <li>• <strong>Active/Inactive:</strong> set in the admin menu under &bdquo;Roster&ldquo; &mdash; see below</li>
                      <li>• <strong>View Stats:</strong> Click player to see season stats</li>
                      <li>• <strong>Trade Consideration:</strong> Identify trade candidates</li>
                      <li>• <strong>Lineup Planning:</strong> Plan your game rotations</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-orange-600 dark:text-orange-400">
                  📋 {t('admin.gmDocs.rosterTitle')}
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-5">
                  {t('admin.gmDocs.rosterIntro')}
                </p>

                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h4 className="font-semibold mb-3">{t('admin.gmDocs.rosterWhere')}</h4>
                    <ol className="space-y-3">
                      {['open', 'toggle', 'undo'].map((step, i) => (
                        <li key={step} className="flex">
                          <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5 flex-shrink-0">
                            {i + 1}
                          </span>
                          <span>{t(`admin.gmDocs.rosterWhereSteps.${step}`)}</span>
                        </li>
                      ))}
                    </ol>

                    <h4 className="font-semibold mt-6 mb-3">{t('admin.gmDocs.rosterNotes')}</h4>
                    <ul className="space-y-2">
                      {['noLimit', 'anytime', 'reset', 'perSeason'].map(note => (
                        <li key={note}>• {t(`admin.gmDocs.rosterNotesList.${note}`)}</li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">{t('admin.gmDocs.rosterEffect')}</h4>
                    <ul className="space-y-2">
                      {['hidden', 'leaders', 'entry', 'kept', 'visible'].map(effect => (
                        <li key={effect}>• {t(`admin.gmDocs.rosterEffectList.${effect}`)}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-purple-600">Team Performance</h3>
                <div className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">Tracking Your Record:</h4>
                    <ul className="mt-2 space-y-1 ml-4">
                      <li>• Check the Standings page for your current record</li>
                      <li>• View wins, losses, and winning percentage</li>
                      <li>• See your conference ranking</li>
                      <li>• Monitor playoff positioning</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">Season Goals:</h4>
                    <ul className="mt-2 space-y-1 ml-4">
                      <li>• Aim for playoff qualification (usually top 8)</li>
                      <li>• Build team chemistry through consistent lineups</li>
                      <li>• Develop your players' skills over the season</li>
                      <li>• Make strategic trades to improve weak areas</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        );

      case 'trades':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">{t('admin.gmDocs.tradesTitle')}</h2>
            
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-purple-800 mb-2">🔄 Trade System</h3>
              <p className="text-purple-700 text-sm">
                {t('admin.gmDocs.tradesDesc')} All trades require admin approval before taking effect.
              </p>
            </div>

            <div className="space-y-6">
              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-blue-600 dark:text-blue-400">Proposing a Trade</h3>
                <ol className="space-y-3 text-sm">
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                    <div>
                      <strong>Navigate to My Trades</strong><br/>
                      Admin Dashboard → My Trades (team owner section)
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                    <div>
                      <strong>Click "Propose New Trade"</strong><br/>
                      This opens the trade proposal form
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                    <div>
                      <strong>Select Trading Partner</strong><br/>
                      Choose which team you want to trade with
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">4</span>
                    <div>
                      <strong>Choose Players</strong><br/>
                      Select players you're offering and players you want
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">5</span>
                    <div>
                      <strong>Add Trade Message</strong><br/>
                      Explain why this trade benefits both teams
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">6</span>
                    <div>
                      <strong>Submit Proposal</strong><br/>
                      Send the trade to the other team and admins
                    </div>
                  </li>
                </ol>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-green-600 dark:text-green-400">Trade Evaluation Tips</h3>
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h4 className="font-semibold mb-3 text-green-600 dark:text-green-400">Good Trade Practices:</h4>
                    <ul className="space-y-2">
                      <li>• Match similar overall ratings</li>
                      <li>• Consider positional needs</li>
                      <li>• Trade depth for quality or vice versa</li>
                      <li>• Include clear reasoning in your message</li>
                      <li>• Be respectful to other GMs</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-red-600 dark:text-red-400">Avoid These Mistakes:</h4>
                    <ul className="space-y-2">
                      <li>• Severely unbalanced trades</li>
                      <li>• Trading away all your best players</li>
                      <li>• Proposing unrealistic deals</li>
                      <li>• Rushing into trades without thinking</li>
                      <li>• Making too many trade offers</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-orange-600">Trade Status & Process</h3>
                <div className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">📋 Trade Statuses:</h4>
                    <ul className="mt-2 space-y-1 ml-4">
                      <li>• <strong>Pending:</strong> Waiting for admin review</li>
                      <li>• <strong>Approved:</strong> Accepted by admins, players swapped</li>
                      <li>• <strong>Rejected:</strong> Declined by admins with reason</li>
                      <li>• <strong>Expired:</strong> Too old, no longer valid</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">⏱️ Timeline:</h4>
                    <ul className="mt-2 space-y-1 ml-4">
                      <li>• Proposals reviewed within 24-48 hours</li>
                      <li>• Approved trades take effect immediately</li>
                      <li>• Players can be used in next game after approval</li>
                      <li>• Trade deadline usually before playoffs</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        );

      case 'games':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">{t('admin.gmDocs.gamesTitle')}</h2>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-green-800 mb-2">📊 Game Results</h3>
              <p className="text-green-700 dark:text-green-400 text-sm">
                {t('admin.gmDocs.gamesDesc')} You can enter results manually or upload screenshots for automatic extraction.
              </p>
            </div>

            <div className="space-y-6">
              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-blue-600 dark:text-blue-400">Entering Game Results - Manual</h3>
                <ol className="space-y-3 text-sm">
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                    <div>
                      <strong>Navigate to My Games</strong><br/>
                      Admin Dashboard → My Games (in team owner section)
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                    <div>
                      <strong>Find Your Game</strong><br/>
                      Look for games with "Enter Result" button
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                    <div>
                      <strong>Enter Final Score</strong><br/>
                      Input the final score for both teams
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">4</span>
                    <div>
                      <strong>Add Player Statistics</strong><br/>
                      Click "Add Player Stats" to enter detailed stats
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">5</span>
                    <div>
                      <strong>Submit Results</strong><br/>
                      Review and submit to update league standings
                    </div>
                  </li>
                </ol>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-purple-600">Using OCR Screenshots</h3>
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h4 className="font-semibold mb-3 text-green-600 dark:text-green-400">📸 Taking Screenshots:</h4>
                    <ul className="space-y-2">
                      <li>• Go to box score in NBA 2K after game</li>
                      <li>• Take clear, high-resolution screenshots</li>
                      <li>• Capture both teams' stats separately</li>
                      <li>• Ensure all player names and stats are visible</li>
                      <li>• Avoid glare and poor lighting</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-blue-600 dark:text-blue-400">⚡ Upload Process:</h4>
                    <ul className="space-y-2">
                      <li>• Click "Enter Stats" on your game</li>
                      <li>• Select "Upload Screenshots" tab</li>
                      <li>• Upload home and away team screenshots</li>
                      <li>• Click "Extract Stats" button</li>
                      <li>• Review and correct any errors</li>
                      <li>• Submit when everything looks correct</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-orange-600">Player Statistics</h3>
                <div className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">📈 Required Stats:</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                      <ul className="space-y-1">
                        <li>• Minutes Played</li>
                        <li>• Points</li>
                        <li>• Rebounds</li>
                        <li>• Assists</li>
                      </ul>
                      <ul className="space-y-1">
                        <li>• Field Goals Made</li>
                        <li>• Field Goals Attempted</li>
                        <li>• 3-Point Made</li>
                        <li>• 3-Point Attempted</li>
                      </ul>
                      <ul className="space-y-1">
                        <li>• Free Throws Made</li>
                        <li>• Free Throws Attempted</li>
                        <li>• Offensive Rebounds</li>
                        <li>• Steals</li>
                      </ul>
                      <ul className="space-y-1">
                        <li>• Blocks</li>
                        <li>• Turnovers</li>
                        <li>• Fouls</li>
                      </ul>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">⚠️ Important Notes:</h4>
                    <ul className="mt-2 space-y-1 ml-4">
                      <li>• Enter stats for all players who played</li>
                      <li>• Double-check shooting percentages</li>
                      <li>• Ensure points math adds up correctly</li>
                      <li>• Submit results within league deadline</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        );

      case 'schedule':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">My Schedule</h2>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-800 mb-2">📅 Schedule Management</h3>
              <p className="text-blue-700 dark:text-blue-400 text-sm">
                View your upcoming games, coordinate with opponents, and track your team's progress throughout the season.
              </p>
            </div>

            <div className="space-y-6">
              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-blue-600 dark:text-blue-400">Viewing Your Schedule</h3>
                <ol className="space-y-3 text-sm">
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                    <div>
                      <strong>Navigate to My Games</strong><br/>
                      Admin Dashboard → My Games (team owner section)
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                    <div>
                      <strong>Filter by Status</strong><br/>
                      View Scheduled, Completed, or All games
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                    <div>
                      <strong>Check Game Details</strong><br/>
                      See opponent, date, time, and home/away status
                    </div>
                  </li>
                </ol>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-green-600 dark:text-green-400">Coordinating Games</h3>
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h4 className="font-semibold mb-3">📞 Contacting Opponents:</h4>
                    <ul className="space-y-2">
                      <li>• Check Team Owners page for contact info</li>
                      <li>• Look for Twitch profiles or gamertags</li>
                      <li>• Use league chat or messaging system</li>
                      <li>• Coordinate game times that work for both</li>
                      <li>• Confirm platform compatibility</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">🎮 Game Setup:</h4>
                    <ul className="space-y-2">
                      <li>• Agree on specific date and time</li>
                      <li>• Determine home/away team advantages</li>
                      <li>• Set up NBA 2K online match</li>
                      <li>• Use correct team rosters for your league</li>
                      <li>• Follow league rules and settings</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-purple-600">Schedule Management Tips</h3>
                <div className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">⏰ Time Management:</h4>
                    <ul className="mt-2 space-y-1 ml-4">
                      <li>• Check your schedule weekly</li>
                      <li>• Plan games around your availability</li>
                      <li>• Contact opponents early in the week</li>
                      <li>• Be flexible with timing when possible</li>
                      <li>• Respect deadline for game completion</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">📋 Best Practices:</h4>
                    <ul className="mt-2 space-y-1 ml-4">
                      <li>• Be punctual for scheduled games</li>
                      <li>• Have backup times ready</li>
                      <li>• Communicate if you need to reschedule</li>
                      <li>• Keep your gamertag information updated</li>
                      <li>• Follow up on game results promptly</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        );

      case 'playoffs':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">Playoff Games</h2>
            
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-yellow-800 mb-2">🏆 Playoffs Overview</h3>
              <p className="text-yellow-700 dark:text-yellow-400 text-sm">
                Playoffs are the culmination of the regular season. Only the top teams qualify, and games become series-based with elimination rounds.
              </p>
            </div>

            <div className="space-y-6">
              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-blue-600 dark:text-blue-400">Qualifying for Playoffs</h3>
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h4 className="font-semibold mb-3 text-green-600 dark:text-green-400">📊 Qualification:</h4>
                    <ul className="space-y-2">
                      <li>• Usually top 8 teams per conference</li>
                      <li>• Based on regular season win-loss record</li>
                      <li>• Seeding determines playoff matchups</li>
                      <li>• Higher seeds get home-court advantage</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-blue-600 dark:text-blue-400">🎯 Preparation:</h4>
                    <ul className="space-y-2">
                      <li>• Monitor standings throughout season</li>
                      <li>• Focus on wins in final weeks</li>
                      <li>• Consider strategic trades before deadline</li>
                      <li>• Plan your playoff roster carefully</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-purple-600">Playoff Series Format</h3>
                <div className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">🏅 Series Types:</h4>
                    <ul className="mt-2 space-y-1 ml-4">
                      <li>• <strong>Best of 3:</strong> First to win 2 games advances</li>
                      <li>• <strong>Best of 5:</strong> First to win 3 games advances</li>
                      <li>• <strong>Best of 7:</strong> First to win 4 games advances</li>
                      <li>• <strong>Single Elimination:</strong> One game, winner takes all</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">🏠 Home Court Advantage:</h4>
                    <ul className="mt-2 space-y-1 ml-4">
                      <li>• Higher seed gets more home games</li>
                      <li>• Game 1 always at higher seed's "home"</li>
                      <li>• Series alternates between home venues</li>
                      <li>• Higher seed gets final game at home if needed</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-orange-600">Playing Playoff Games</h3>
                <ol className="space-y-3 text-sm">
                  <li className="flex">
                    <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                    <div>
                      <strong>Check Playoff Bracket</strong><br/>
                      View the playoff bracket to see your matchup and series status
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                    <div>
                      <strong>Coordinate with Opponent</strong><br/>
                      Contact your series opponent to schedule games
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                    <div>
                      <strong>Play Games in Order</strong><br/>
                      Complete games 1, 2, 3, etc. in sequence
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">4</span>
                    <div>
                      <strong>Enter Results Promptly</strong><br/>
                      Submit each game result to advance the series
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">5</span>
                    <div>
                      <strong>Advance or Get Eliminated</strong><br/>
                      Winner advances to next round, loser is eliminated
                    </div>
                  </li>
                </ol>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-red-600 dark:text-red-400">Playoff Strategy</h3>
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h4 className="font-semibold mb-3 text-green-600 dark:text-green-400">🧠 Strategic Considerations:</h4>
                    <ul className="space-y-2">
                      <li>• Analyze opponent's roster and playstyle</li>
                      <li>• Adjust lineups for specific matchups</li>
                      <li>• Consider rest vs. rhythm for key players</li>
                      <li>• Plan for must-win elimination games</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-blue-600 dark:text-blue-400">🎮 Game Execution:</h4>
                    <ul className="space-y-2">
                      <li>• Focus on ball movement and teamwork</li>
                      <li>• Limit turnovers in crucial moments</li>
                      <li>• Execute clutch plays under pressure</li>
                      <li>• Stay composed throughout the series</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        );

      case 'settings':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">Settings & Preferences</h2>
            
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">⚙️ Personal Settings</h3>
              <p className="text-gray-700 dark:text-gray-300 text-sm">
                Manage your account preferences, notifications, and league-specific settings to customize your experience.
              </p>
            </div>

            <div className="space-y-6">
              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-blue-600 dark:text-blue-400">Account Settings</h3>
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h4 className="font-semibold mb-3">👤 Profile Settings:</h4>
                    <ul className="space-y-2">
                      <li>• <strong>Username:</strong> Your unique league identifier</li>
                      <li>• <strong>Email:</strong> For league communications</li>
                      <li>• <strong>First Name:</strong> Display name for other GMs</li>
                      <li>• <strong>Password:</strong> Change your login password</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">🎮 Gaming Settings:</h4>
                    <ul className="space-y-2">
                      <li>• <strong>Gamertag:</strong> Your NBA 2K username</li>
                      <li>• <strong>Platform:</strong> Xbox or PlayStation</li>
                      <li>• <strong>Twitch Profile:</strong> Stream integration</li>
                      <li>• <strong>Time Zone:</strong> For scheduling coordination</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-green-600 dark:text-green-400">Notification Preferences</h3>
                <div className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">📧 Email Notifications:</h4>
                    <ul className="mt-2 space-y-1 ml-4">
                      <li>• Trade proposals and responses</li>
                      <li>• Game scheduling reminders</li>
                      <li>• League announcements</li>
                      <li>• Playoff qualification updates</li>
                      <li>• System maintenance notices</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">🔔 In-App Notifications:</h4>
                    <ul className="mt-2 space-y-1 ml-4">
                      <li>• Real-time trade updates</li>
                      <li>• Game result confirmations</li>
                      <li>• Schedule changes</li>
                      <li>• League chat messages</li>
                      <li>• Admin announcements</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-purple-600">Privacy & Visibility</h3>
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h4 className="font-semibold mb-3">👁️ Public Information:</h4>
                    <ul className="space-y-2">
                      <li>• Team ownership and record</li>
                      <li>• First name and username</li>
                      <li>• Twitch profile (if provided)</li>
                      <li>• Gaming platform</li>
                      <li>• League participation history</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">🔒 Private Information:</h4>
                    <ul className="space-y-2">
                      <li>• Email address</li>
                      <li>• Login credentials</li>
                      <li>• Direct contact information</li>
                      <li>• Internal admin notes</li>
                      <li>• Account creation details</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-orange-600">League Preferences</h3>
                <div className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">🏀 Game Preferences:</h4>
                    <ul className="mt-2 space-y-1 ml-4">
                      <li>• Preferred game times and days</li>
                      <li>• Communication method preferences</li>
                      <li>• Streaming/recording preferences</li>
                      <li>• Matchup availability windows</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">📊 Display Preferences:</h4>
                    <ul className="mt-2 space-y-1 ml-4">
                      <li>• Language selection (English/German)</li>
                      <li>• Stat display formats</li>
                      <li>• Dashboard layout options</li>
                      <li>• Team logo and color preferences</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        );

      default:
        return <div>Select a section from the sidebar</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-700 flex">
      {/* Sidebar */}
      <div className="w-80 bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">📚 {t('admin.gmDocs.title')}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('admin.gmDocs.subtitle')}</p>
        </div>
        
        <nav className="p-4">
          <div className="space-y-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center ${
                  activeSection === section.id
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 border border-blue-200 dark:border-blue-700'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:bg-gray-700'
                }`}
              >
                <span className="text-lg mr-3">{section.icon}</span>
                <span className="font-medium">{section.title}</span>
              </button>
            ))}
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-8">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default GMDocumentation;